import PropTypes from 'prop-types';
import React from 'react';
import { merge, fromEvent } from 'rxjs';
import { tap, map, mergeMap, filter, throttleTime, takeUntil } from 'rxjs/operators';

import { childrenShape } from '@bibliocommons/bc-prop-types';

import { isArrowKey, isTouchEvent, getKeyCodeMovement, hasTwoTouchPoints, normalizeTouch } from './eventUtils';

const MIN_SCALE = 1;

function between(min, max, val) {
  return Math.min(max, Math.max(min, val));
}

function inverse(val) {
  return val * -1;
}

function isZoomed(scale) {
  return scale > 1;
}

/**
 *
 * Usage:
 *    <PinchHandler scale={1} x={0} y={0} onUpdate={({ x y, scale }) => this.setState({ x, y, scale })}>
 *      <img src="" style={{}} />
 *    </PinchHandler>
 */

export default class PinchHandler extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isPinching: false
    };
  }

  componentDidMount() {
    this.handlePinch();
    this.handlePanWithKeyboard();
    this.handleWheel();
    this.resize();
    this.resize$ = fromEvent(window, 'resize')
      .pipe(throttleTime(500))
      .subscribe(this.resize);
  }

  componentWillUnmount() {
    const observables = ['pinch', 'resize', 'wheel', 'keyboard'];

    observables.forEach(observableName => {
      const obsv$ = this[`${observableName}$`];
      if (obsv$) {
        obsv$.unsubscribe();
        this[`${observableName}$`] = null;
      }
    });

    this._root = null;
  }

  resize() {
    if (this._root) {
      const domNode = this._root;
      this.setState({
        size: {
          width: domNode.offsetWidth,
          height: domNode.offsetHeight
        }
      });
    }
  }

  handleWheel() {
    const domNode = this._root;
    const wheel$ = fromEvent(domNode, 'wheel').pipe(
      map(e => {
        const { scale, maxScale } = this.props;

        if (e.ctrlKey) {
          e.preventDefault();
          const mod = Math.exp(-e.deltaY / 100);

          return between(MIN_SCALE, maxScale, mod * scale);
        }

        return scale;
      })
    );

    this.wheel$ = wheel$.subscribe(newScale => {
      const { scale, x, y, onUpdate } = this.props;
      if (scale !== newScale) {
        onUpdate({
          scale: newScale,
          x: scale === MIN_SCALE ? 0 : x,
          y: scale === MIN_SCALE ? 0 : y
        });
      }
    });
  }

  handlePanWithKeyboard() {
    const domNode = this._root;
    const keydown$ = fromEvent(domNode, 'keydown').pipe(
      filter(isArrowKey),
      map(e => {
        e.preventDefault();

        const { scale, x, y } = this.props;
        const { size } = this.state;
        const movement = getKeyCodeMovement(e).map(val => val * 25);
        const [xDelta, yDelta] = movement;

        const scaleFactorX = (size.width * scale - size.width) / (scale * 2);
        const scaleFactorY = (size.height * scale - size.height) / (scale * 2);
        return {
          x: between(inverse(scaleFactorX), scaleFactorX, x + xDelta),
          y: between(inverse(scaleFactorY), scaleFactorY, y + yDelta),
          scale
        };
      })
    );

    this.keyboard$ = keydown$.subscribe(this.props.onUpdate);
  }

  handlePinch() {
    const domNode = this._root;
    const touchStart = merge(fromEvent(domNode, 'touchstart'), fromEvent(domNode, 'mousedown'));
    const touchMove = merge(fromEvent(document, 'touchmove'), fromEvent(document, 'mousemove'));
    const touchEnd = merge(fromEvent(document, 'touchend'), fromEvent(document, 'mouseup'));

    const translatePos = (point, size) => ({
      x: point.x - size.width / 2,
      y: point.y - size.height / 2
    });

    const pinch$ = touchStart.pipe(
      tap(event => {
        const { scale } = this.props;
        if (hasTwoTouchPoints(event) || isZoomed(scale)) {
          event.preventDefault();
        }
      }),
      mergeMap(md => {
        const startPoint = normalizeTouch(md);
        const { size } = this.state;

        return touchMove.pipe(
          map(mm => {
            const { scale, x, y, maxScale } = this.props;
            const movePoint = normalizeTouch(mm);

            if (hasTwoTouchPoints(mm)) {
              let scaleFactor = 0;
              if (isTouchEvent(mm) && mm.scale) {
                scaleFactor = mm.scale;
              } else if (movePoint.x < size.width / 2) {
                scaleFactor = scale + (translatePos(startPoint, size).x - translatePos(movePoint, size).x) / size.width;
              } else {
                scaleFactor = scale + (translatePos(movePoint, size).x - translatePos(startPoint, size).x) / size.width;
              }

              const nextScale = between(1, maxScale, scaleFactor);
              return {
                scale: nextScale,
                x: nextScale < 1.01 ? 0 : x,
                y: nextScale < 1.01 ? 0 : y
              };
            }

            const scaleFactorX = (size.width * scale - size.width) / (maxScale * 2);
            const scaleFactorY = (size.height * scale - size.height) / (maxScale * 2);
            return {
              x: between(inverse(scaleFactorX), scaleFactorX, movePoint.x - startPoint.x),
              y: between(inverse(scaleFactorY), scaleFactorY, movePoint.y - startPoint.y)
            };
          }),
          takeUntil(touchEnd)
        );
      })
    );

    this.pinch$ = pinch$.subscribe(newObject => {
      if (this.props.scale !== newObject.scale) {
        this.refreshPinchTimeoutTimer();
      }
      window.requestAnimationFrame(() => {
        this.props.onUpdate({
          x: this.props.x,
          y: this.props.y,
          scale: this.props.scale,
          ...newObject
        });
      });
    });
  }

  refreshPinchTimeoutTimer() {
    if (this.pinchTimeoutTimer) {
      clearTimeout(this.pinchTimeoutTimer);
    }

    if (!this.state.isPinching) {
      this.pinchStarted();
    }

    this.pinchTimeoutTimer = setTimeout(() => this.pinchStopped(), 500);
  }

  pinchStarted() {
    this.setState(
      {
        isPinching: true
      },
      this.props.onPinchStart
    );
  }

  pinchStopped() {
    this.setState(
      {
        isPinching: false
      },
      this.props.onPinchStop
    );
  }

  render() {
    return (
      <div
        ref={el => {
          this._root = el;
        }}
      >
        {this.props.children}
      </div>
    );
  }
}

PinchHandler.defaultProps = {
  scale: 1,
  maxScale: 2,
  x: 0,
  y: 0,
  onPinchStart: f => f,
  onPinchStop: f => f
};

PinchHandler.propTypes = {
  onPinchStart: PropTypes.func,
  onPinchStop: PropTypes.func,
  scale: PropTypes.number,
  maxScale: PropTypes.number,
  x: PropTypes.number,
  y: PropTypes.number,
  onUpdate: PropTypes.func,
  children: childrenShape
};
